import { todayISO } from '../calc/format.js';
import { searchProducts } from '../calc/search.js';
import { NONE, exactGroup } from '../calc/selection.js';
import { WEEKDAY_SLUG, clockWindow, selectedDays } from '../calc/timefilter.js';
import { el } from './dom.js';
import { currentFilter } from './filters.js';
import { state } from './state.js';
import { marginSource } from './tables.js';
import { XLSX_TYPE, xlsxParts } from '../calc/xlsx.js';

/* --------------------------------------------------------------------------
   Export till Excel (.xlsx)

   Varje lista laddas ned som en Excel-fil med ett blad. Kolumnerna har typ
   (text, heltal, tal, procent, datum), så att talen går att räkna med direkt.
   XML-filerna skrivs i calc/xlsx.js; här packas de med JSZip, som sidan
   redan laddar för zip-exporterna. Allt sker i webbläsaren.
   -------------------------------------------------------------------------- */

/**
 * @param {string} filename  utan ändelse
 * @param {{sheet: string, columns: {h: string, t?: string}[], rows: any[][]}} table
 */
export async function downloadXlsx(filename, table){
  const zip = new JSZip();
  for(const [name, xml] of Object.entries(xlsxParts(table))) zip.file(name, xml);
  const bytes = await zip.generateAsync({type: 'uint8array', compression: 'DEFLATE'});
  const blob = new Blob([bytes], {type: XLSX_TYPE});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename + '.xlsx';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Del av ett filnamn: "Försäljning||Adapter" -> "forsaljning-adapter". */
export function slug(v){
  return String(v).normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
}

/** Filnamnets periodsdel. Med artikelgrupp eller leverantör vald följer de
    med i namnet, så att en export per leverantör inte heter som nästa. */
export function periodSlug(){
  const f = currentFilter();
  let base;
  if(f.mode === 'today')      base = todayISO();
  else if(f.mode === 'date')  base = f.date || 'datum';
  else if(f.mode === 'month') base = f.month || 'manad';
  else{
    let a = f.from, b = f.to;
    if(a && b && a > b) [a,b] = [b,a];
    base = `${a || 'start'}_${b || 'slut'}`;
  }
  return base + selectionSlug();
}

export function selectionSlug(){
  const f = currentFilter();
  let out = '';
  const g = f.groups, s = f.suppliers, c = f.customers;
  if(c.length === 1)     out += '-' + (c[0] === NONE ? 'utan-kund' : 'kund-' + slug(c[0]));
  else if(c.length > 1)  out += `-${c.length}-kunder`;
  if(g.length === 1)     out += '-' + (g[0] === NONE ? 'utan-grupp'
                                   : exactGroup(g[0]) !== null ? slug(exactGroup(g[0])) + '-utan-undergrupp'
                                   : slug(g[0]));
  else if(g.length > 1)  out += `-${g.length}-grupper`;
  if(s.length === 1)     out += '-' + (s[0] === NONE ? 'utan-leverantor' : 'lev-' + slug(s[0]));
  else if(s.length > 1)  out += `-${s.length}-leverantorer`;
  // Veckodagar och klockslag: "-fre-lor-son-1600-2100"
  const days = selectedDays(f.weekdays);
  if(days) out += '-' + [...days].sort().map(d => WEEKDAY_SLUG[d]).join('-');
  const w = clockWindow(f.timeFrom, f.timeTo);
  if(w) out += '-' + (f.timeFrom || '0000').replace(/:/g,'') + '-' + (f.timeTo || '2400').replace(/:/g,'');
  return out;
}

/** Artikellistan exporteras som den visas: med sökningen, om kunden sökt. */
export function exportProducts(){
  const s = state.stats;
  const {items, query} = searchProducts([...s.products.values()], state.productQuery);
  const rows = items
    .sort((a,b) => b.revenue - a.revenue)
    .map(p => [
      p.articleNumber, p.name, p.isService ? 'Tjänst' : 'Vara',
      // Utan inköpspris i filen finns ingen bruttovinst att exportera
      p.units, p.revenue, p.revenueEx, state.hasCost ? p.profit : null,
      // Marginalen utelämnas när inköpspris saknas, annars ser den ut som 100 %
      (p.revenueEx && !p.noCost) ? p.profit/p.revenueEx*100 : null,
      p.noCost ? 'Inköpspris saknas' : '',
      p.lastSold
    ]);
  return downloadXlsx(`easycashier-artiklar-${periodSlug()}${query ? '-sokning' : ''}`, {
    sheet: 'Artiklar',
    columns: [{h:'Artikelnummer'}, {h:'Benämning'}, {h:'Typ'}, {h:'Antal', t:'num'},
      {h:'Omsättning inkl. moms', t:'num'}, {h:'Omsättning exkl. moms', t:'num'}, {h:'Bruttovinst', t:'num'},
      {h:'Marginal %', t:'pct'}, {h:'Anmärkning'}, {h:'Senast såld', t:'date'}],
    rows
  });
}

export function exportMargin(){
  const isProduct = el('mGroup').value === 'product';
  const label = el('mGroup').selectedOptions[0].textContent;
  const rows = [...marginSource().entries()]
    .map(([k,v]) => ({name: isProduct ? v.name : k, ...v}))
    .sort((a,b) => b.profit - a.profit)          // sortera på talet, inte på texten
    .map(v => [v.name, v.units, v.revenue, v.revenueEx, v.profit, v.revenueEx ? v.profit/v.revenueEx*100 : null]);
  return downloadXlsx(`easycashier-marginal-${periodSlug()}`, {
    sheet: 'Marginal',
    columns: [{h:label}, {h:'Antal', t:'num'}, {h:'Omsättning inkl. moms', t:'num'},
      {h:'Omsättning exkl. moms', t:'num'}, {h:'Bruttovinst', t:'num'}, {h:'Marginal %', t:'pct'}],
    rows
  });
}
